/* eslint-disable react/function-component-definition */
import React from 'react';
import clsx from 'clsx';

import { useFocus } from '../../common/hooks';
import { useButtonBubbles } from '../Button/useButtonBubbles';
import { Spinner, SpinnerProps, SpinnerSize } from '../Spinner';
import { ButtonPropsWithoutRef } from '../../common/types';
import { Icon, IconSize } from '../Icon';

export enum IconButtonSize {
  small = 'small',
  medium = 'medium',
  large = 'large'
}

export enum IconButtonVariant {
  default = 'default',
  secondary = 'secondary',
  ghost = 'ghost',
  pagination = 'pagination'
}

export type IconButtonSpinnerVariant = 'done' | boolean | number;

export type IconButtonProps<ComponentType extends React.ElementType> = {
  /**
   * Базовый компонент
   *
   * @type React.ElementType
   */
  component?: ComponentType;
  /**
   * Размер IconButton (`small`, `medium`, `large`)
   */
  size?: IconButtonSize;
  /**
   * Вариант оформления Button (`default`, `ghost`, `pagination`)
   */
  variant?: IconButtonVariant;
  /**
   * Неактивное состояние Button - состояние, при котором компонент отображается, но недоступен для действий пользователя
   */
  inactive?: boolean;
  /**
   * Заблокированное состояние Button
   */
  disabled?: boolean;
  /**
   * Выбранное состояние кнопки
   */
  selected?: boolean;
  /**
   * Иконка
   */
  icon?: any;
  /**
   * Цвет кнопки
   */
  color?: string;
  /**
   * Цвет иконки
   */
  iconColor?: string;
  /**
   * Spinner
   */
  spinner?: IconButtonSpinnerVariant;
  /**
   * Дополнительные css-классы элементов:
   * * content – контейнер содержимого
   * * icon – класс иконки
   * * spinner – класс спиннера
   */
  classes?: {
    content?: string;
    icon?: string;
    spinner?: string;
  };
  /**
   * Слот для произвольного контента кнопки. Приоритетнее, чем icon.
   */
  children?: React.ReactNode;
  /**
   * Реф на корневой элемент
   */
  innerRef?: React.Ref<HTMLElement>;
} & Omit<React.ComponentProps<ComponentType>, 'size'>;

export function IconButton(props: IconButtonProps<'button'>): JSX.Element;
export function IconButton<ComponentType extends React.ElementType>(
  props: IconButtonProps<ComponentType>
): JSX.Element;
export function IconButton<ComponentType extends React.ElementType>({
  component,
  className,
  size = IconButtonSize.medium,
  variant = IconButtonVariant.default,
  inactive,
  disabled,
  selected,
  children,
  color,
  icon,
  iconColor,
  spinner,
  classes,
  innerRef,
  ...props
}: IconButtonProps<ComponentType>) {
  const [bubbleRootRef, bubblesProps] = useButtonBubbles(
    !disabled && !inactive,
    props
  );

  const { focused, ...focusProps } = useFocus(props);

  const iconSize =
    size === IconButtonSize.small ? IconSize.small : IconSize.medium;

  const getSpinnerProps = () => {
    const spinnerProps: SpinnerProps = {};

    if (spinner === true) {
      spinnerProps.loading = true;
    } else if (spinner === 'done') {
      spinnerProps.done = true;
    } else if (typeof spinner === 'number') {
      spinnerProps.value = spinner;
    }

    spinnerProps.size = SpinnerSize[iconSize];

    return spinnerProps;
  };

  const Base = (component || 'button') as React.ElementType;

  return (
    <Base
      ref={innerRef}
      className={clsx(className, 'hse-Button hse-IconButton', {
        [`hse-Button_variant_${
          variant === 'default' ? 'primary' : variant
        } hse-IconButton_variant_${variant}`]: variant,
        'hse-IconButton_spinner': spinner,
        'hse-IconButton_spinner_done': spinner === 'done',
        [`hse-IconButton_size_${size}`]: size,
        'hse-Button_disabled hse-IconButton_disabled': disabled,
        'hse-Button_inactive hse-IconButton_inactive': inactive,
        'hse-Button_focused hse-IconButton_focused': focused,
        'hse-IconButton_selected': selected,
        'hse-Button_custom-color': Boolean(color)
      })}
      type="button"
      style={{
        color
      }}
      {...props}
      {...bubblesProps}
      {...focusProps}
    >
      <div ref={bubbleRootRef} className="hse-Button__bubbleRoot" />
      <div
        className={clsx(
          classes?.content,
          'hse-Button__content',
          'hse-IconButton__content'
        )}
      >
        {!children ? (
          <>
            {spinner ? (
              <Spinner
                className={clsx(classes?.spinner, 'hse-IconButton__spinner', {
                  'hse-IconButton__spinner_done': spinner === 'done'
                })}
                {...getSpinnerProps()}
              />
            ) : null}
            {!spinner && icon ? (
              <Icon
                className={classes?.icon}
                icon={icon}
                size={iconSize}
                color={
                  !disabled && !inactive && !selected ? iconColor : undefined
                }
              />
            ) : null}
          </>
        ) : (
          children
        )}
      </div>
    </Base>
  );
}

IconButton.Variant = IconButtonVariant;
IconButton.Size = IconButtonSize;
